import statisticsService from './statisticsService';
import statsApi from '../api/statsApi';
import { getDateRangeForPeriod, getPeriodPresetFromDateRange, getBarRequestRange, getBarPeriod, getBarDisplayPeriod, getBarChartRange, getBarChartGrain, generateExpectedKeys, formatBarLabel, getLast5MonthsRangeUTC5, getLast5MonthsKeysUTC5 } from '../utils/statsFilterUtils';
import { cachedRequest } from '../utils/requestCache';

function formatPaymentBarLabel(periodKey, grain) {
  if (!periodKey) return '';
  const str = String(periodKey);
  if (grain === 'day' && str.length >= 10) {
    const date = new Date(str.slice(0, 10) + 'T00:00:00');
    if (!isNaN(date.getTime())) return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  if ((grain === 'month' || str.length >= 7) && str.length >= 7) {
    const [y, m] = str.split('-');
    const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  return str;
}

class StatisticsPageService {
  async getStatsCached(params) {
    return cachedRequest(
      () => statsApi.getStats(params),
      '/api/stats',
      params
    );
  }
  async fetchLoadsStats(filterValues, selectedDateRange) {
    let periodPreset = filterValues.period || getPeriodPresetFromDateRange(selectedDateRange);
    let customStart = null;
    let customEnd = null;
    
    if (periodPreset === 'custom' || selectedDateRange === 'custom') {
      if (filterValues.dateFrom && filterValues.dateTo) {
        const startDate = new Date(filterValues.dateFrom);
        const endDate = new Date(filterValues.dateTo);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        customStart = startDate.toISOString();
        customEnd = endDate.toISOString();
      } else {
        const now = new Date();
        customStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        customEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();
      }
      periodPreset = 'custom';
    }

    const isCustomPreset = periodPreset === 'custom' || selectedDateRange === 'custom';
    let from;
    let to;
    let fromBar;
    let toBar;
    let barGrain;

    if (
      isCustomPreset &&
      typeof filterValues.dateFrom === 'string' &&
      typeof filterValues.dateTo === 'string' &&
      filterValues.dateFrom &&
      filterValues.dateTo
    ) {
      from = filterValues.dateFrom;
      to = filterValues.dateTo;
      fromBar = filterValues.dateFrom;
      toBar = filterValues.dateTo;
      barGrain = getBarChartGrain('custom', filterValues.dateFrom, filterValues.dateTo);
    } else {
      const dateRange = getDateRangeForPeriod(periodPreset, customStart, customEnd);
      from = dateRange.startDate?.slice(0, 10);
      to = dateRange.endDate?.slice(0, 10);

      const barChartRange = getBarChartRange(periodPreset, customStart || '', customEnd || '');
      fromBar = barChartRange.startDate?.slice(0, 10);
      toBar = barChartRange.endDate?.slice(0, 10);
      barGrain = getBarChartGrain(periodPreset, customStart || '', customEnd || '');
    }
    
    const scopeType = (filterValues.scopeType === 'user' || filterValues.scopeType === 'customer' || filterValues.scopeType === 'carrier') ? filterValues.scopeType : 'global';
    const scopeId = filterValues.scopeId ? String(filterValues.scopeId).trim() : null;
    
    const statuses = filterValues.loadStatus && filterValues.loadStatus.length > 0 
      ? filterValues.loadStatus.map(s => {
          const lower = String(s).toLowerCase().replace(/\s+/g, '');
          if (lower === 'pickedup') return 'pickedUp';
          if (lower === 'onhold') return 'onHold';
          if (lower === 'cancelled' || lower === 'canceled') return 'cancelled';
          return lower;
        }).join(',')
      : null;

    const grain = barGrain === 'month' ? 'month' : 'day';
    const loadsParams = { period: 'range', from, to, scopeType, grain };
    if (scopeId) loadsParams.scopeId = scopeId;
    if (statuses) loadsParams.statuses = statuses;

    const barChartParams = { period: 'range', from: fromBar, to: toBar, scopeType, grain };
    if (scopeId) barChartParams.scopeId = scopeId;
    if (statuses) barChartParams.statuses = statuses;

    const isSameRange = from === fromBar && to === toBar && 
                       JSON.stringify(loadsParams) === JSON.stringify(barChartParams);

    const requests = [
      this.getStatsCached(loadsParams).catch(() => ({ data: { success: false, data: { totals: {}, breakdowns: {}, points: [] } } })),
      statsApi.getTopUsers({ from, to, limit: 5 }).catch(() => ({ data: { success: false, data: [] } }))
    ];

    if (!isSameRange) {
      requests.push(
        this.getStatsCached(barChartParams).catch(() => ({ data: { success: false, data: { points: [] } } }))
      );
    }

    const responses = await Promise.all(requests);
    const loadsResponse = responses[0];
    const topUsersResponse = responses[1];
    const barChartResponse = !isSameRange ? responses[2] : null;

    const loadsData = loadsResponse?.data?.success ? loadsResponse.data.data : null;
    const topUsersData = topUsersResponse?.data?.success && Array.isArray(topUsersResponse.data.data) ? topUsersResponse.data.data : [];
    const barChartData = isSameRange ? loadsData : (barChartResponse?.data?.success ? barChartResponse.data.data : null);

    const barChartNormalized = this.processBarChartData(barChartData, periodPreset, customStart, customEnd);
    
    let calculatedTotalFromBarChart = 0;
    if (barChartNormalized?.normalizedPoints && Array.isArray(barChartNormalized.normalizedPoints)) {
      calculatedTotalFromBarChart = barChartNormalized.normalizedPoints.reduce((sum, point) => {
        return sum + (Number(point.total) || 0);
      }, 0);
    }

    const byMonth = this.buildByMonthFromPoints(loadsData?.points || [], barChartNormalized?.barPeriod || 'day');
    
    const loadsStats = loadsData ? {
      total: loadsData.totals?.totalLoads ?? calculatedTotalFromBarChart,
      loadsCreated: loadsData.totals?.loadsCreated ?? loadsData.totals?.totalLoads ?? calculatedTotalFromBarChart,
      byStatus: this.normalizeStatuses(loadsData.breakdowns?.loadsByStatus || {}),
      byMonth,
      dateRange: { startDate: from, endDate: to },
      totalProfit: loadsData.totals?.totalProfit ?? 0,
      calculatedTotalFromBarChart: calculatedTotalFromBarChart,
      expiredListed: loadsData.totals?.expiredListed ?? 0
    } : null;

    return {
      loadsStats,
      top5Users: topUsersData,
      barChartData: barChartNormalized?.normalizedPoints ? { normalizedPoints: barChartNormalized.normalizedPoints, barPeriod: barChartNormalized.barPeriod } : null,
      barChartStatusData: barChartNormalized?.pointsBar || null,
      dashboardStats: loadsData ? {
        overview: {
          totalProfit: loadsData.totals?.totalProfit ?? 0
        }
      } : null
    };
  }

  async fetchUsersStats(filterValues, selectedDateRange) {
    const periodPreset = getPeriodPresetFromDateRange(selectedDateRange);
    const customStart = filterValues.dateFrom ? new Date(filterValues.dateFrom).toISOString() : null;
    const customEnd = filterValues.dateTo ? new Date(filterValues.dateTo).toISOString() : null;
    const usersRange = getDateRangeForPeriod(periodPreset, customStart, customEnd);
    const from = usersRange.startDate?.slice(0, 10);
    const to = usersRange.endDate?.slice(0, 10);
    const chartPeriod = (periodPreset === 'day' || periodPreset === 'week' || periodPreset === 'month') ? 'day' : 'month';
    const scopeType = filterValues.scopeType === 'user' && filterValues.scopeId && /^[a-fA-F0-9]{24}$/.test(String(filterValues.scopeId).trim()) ? 'user' : 'global';
    const scopeId = scopeType === 'user' ? String(filterValues.scopeId).trim() : undefined;

    const statsParams = { period: 'range', from, to, scopeType };
    if (scopeId) statsParams.scopeId = scopeId;

    const barChartRange = getBarChartRange(periodPreset, customStart || '', customEnd || '');
    const fromBar = barChartRange.startDate?.slice(0, 10);
    const toBar = barChartRange.endDate?.slice(0, 10);
    const barGrain = getBarChartGrain(periodPreset, customStart || '', customEnd || '');
    const barChartParams = { period: 'range', from: fromBar, to: toBar, scopeType };
    if (scopeId) barChartParams.scopeId = scopeId;

    const fiveMonths = getLast5MonthsRangeUTC5();
    const fiveMonthsParams = { period: 'range', from: fiveMonths.startDate.slice(0, 10), to: fiveMonths.endDate.slice(0, 10), scopeType };
    if (scopeId) fiveMonthsParams.scopeId = scopeId;

    const grain = barGrain === 'month' ? 'month' : 'day';
    statsParams.grain = grain;
    barChartParams.grain = grain;
    fiveMonthsParams.grain = 'month';

    const isStatsSameAsBar = from === fromBar && to === toBar && JSON.stringify(statsParams) === JSON.stringify(barChartParams);

    const requests = [
      this.getStatsCached(statsParams).catch(error => {
        if (error?.response?.status === 403) {
          throw new Error('Access denied');
        }
        throw error;
      }),
      statsApi.getTopUsers({ from, to, limit: 5 }).catch(() => ({ data: { success: false, data: [] } })),
      this.getStatsCached(fiveMonthsParams).catch(() => ({ data: { success: false, data: { points: [] } } }))
    ];

    if (!isStatsSameAsBar) {
      requests.splice(2, 0, this.getStatsCached(barChartParams).catch(() => ({ data: { success: false, data: { points: [] } } })));
    }

    const responses = await Promise.all(requests);
    const statsResponse = responses[0];
    const topUsersResponse = responses[1];
    const barChartResponse = isStatsSameAsBar ? null : responses[2];
    const fiveMonthsResponse = isStatsSameAsBar ? responses[2] : responses[3];

    const statsData = statsResponse?.data?.success ? statsResponse.data.data : null;
    const topUsersData = topUsersResponse?.data?.success && Array.isArray(topUsersResponse.data.data) ? topUsersResponse.data.data : [];
    const barChartData = isStatsSameAsBar ? statsData : (barChartResponse?.data?.success ? barChartResponse.data.data : null);
    const fiveMonthsData = fiveMonthsResponse?.data?.success ? fiveMonthsResponse.data.data : null;

    let byMonth = [];
    if (fiveMonthsData?.points && Array.isArray(fiveMonthsData.points)) {
      const expectedKeys = getLast5MonthsKeysUTC5();
      const responseMap = {};
      fiveMonthsData.points.forEach((p) => {
        const key = p.periodKey ? String(p.periodKey).slice(0, 7) : '';
        if (!key || key.length < 7) return;
        if (!responseMap[key]) {
          const date = new Date(key + '-01');
          responseMap[key] = { 
            month: key, 
            monthLabel: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), 
            total: 0, 
            delivered: 0, 
            taken: 0 
          };
        }
        const t = p.totals || {};
        const b = p.breakdowns || {};
        const byStatus = b.loadsByStatus || {};
        responseMap[key].total += t.totalLoads ?? t.loadsCreated ?? 0;
        responseMap[key].delivered += byStatus.delivered ?? 0;
        responseMap[key].taken += (byStatus.dispatched ?? 0) + (byStatus.pickedUp ?? 0) + (byStatus.delivered ?? 0) + (byStatus.onHold ?? 0);
      });
      byMonth = expectedKeys.map((key) => {
        if (responseMap[key]) {
          return responseMap[key];
        }
        const date = new Date(key + '-01');
        return { 
          month: key, 
          monthLabel: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), 
          total: 0, 
          delivered: 0, 
          taken: 0 
        };
      });
    }

    const loadsStats = statsData ? {
      loadsCreated: statsData.totals?.loadsCreated ?? statsData.totals?.totalLoads ?? 0,
      total: statsData.totals?.totalLoads ?? statsData.totals?.loadsCreated ?? 0,
      byStatus: this.normalizeStatuses(statsData.breakdowns?.loadsByStatus || {}),
      byMonth,
      totalProfit: statsData.totals?.totalProfit ?? 0,
      expiredListed: statsData.totals?.expiredListed ?? 0,
      chartPoints: statsData.points?.map((p) => ({
        periodKey: p.periodKey,
        loadsCreated: p.totals?.loadsCreated ?? p.totals?.totalLoads ?? 0,
        delivered: p.breakdowns?.loadsByStatus?.delivered ?? 0
      })) || []
    } : null;

    const barChartNormalized = this.processBarChartData(barChartData, periodPreset, customStart, customEnd);

    return {
      loadsStats,
      top5Users: topUsersData,
      barChartData: barChartNormalized?.normalizedPoints ? { normalizedPoints: barChartNormalized.normalizedPoints, barPeriod: barChartNormalized.barPeriod } : null,
      barChartStatusData: barChartNormalized?.pointsBar || null,
      dashboardStats: {
        overview: {
          totalProfit: statsData?.totals?.totalProfit ?? 0,
          delivered: statsData?.breakdowns?.loadsByStatus?.delivered ?? 0
        }
      }
    };
  }

  async fetchPaymentsStats(filterValues, selectedDateRange, selectedTab) {
    const periodPreset = getPeriodPresetFromDateRange(selectedDateRange);
    const customStart = filterValues.dateFrom ? new Date(filterValues.dateFrom).toISOString() : null;
    const customEnd = filterValues.dateTo ? new Date(filterValues.dateTo).toISOString() : null;
    const dateRange = getDateRangeForPeriod(periodPreset, customStart, customEnd);
    const from = dateRange.startDate?.slice(0, 10);
    const to = dateRange.endDate?.slice(0, 10);

    const barChartRange = getBarChartRange(periodPreset, customStart || '', customEnd || '');
    const fromBar = barChartRange.startDate?.slice(0, 10);
    const toBar = barChartRange.endDate?.slice(0, 10);
    const barGrain = getBarChartGrain(periodPreset, customStart || '', customEnd || '');
    const grain = barGrain === 'month' ? 'month' : 'day';

    const paymentType = filterValues.paymentType || (selectedTab === 'payable' ? 'payable' : selectedTab === 'receivable' ? 'receivable' : null);
    const scopeType = (filterValues.scopeType === 'user' || filterValues.scopeType === 'customer' || filterValues.scopeType === 'carrier') && filterValues.scopeId ? filterValues.scopeType : 'global';
    const scopeId = scopeType !== 'global' ? String(filterValues.scopeId || '').trim() : null;

    const buildParams = (f, t) => {
      const p = { period: 'range', from: f, to: t, scopeType, grain };
      if (paymentType) p.paymentType = paymentType;
      if (scopeId) p.scopeId = scopeId;
      if (filterValues.paymentStatus && filterValues.paymentStatus.length > 0) {
        p.paymentStatus = filterValues.paymentStatus.map(s => String(s).toLowerCase()).join(',');
      }
      return p;
    };

    const isSameRange = from === fromBar && to === toBar;
    const requests = [this.getStatsCached(buildParams(from, to)).catch(() => ({ data: { success: false } }))];
    if (!isSameRange) {
      requests.push(this.getStatsCached(buildParams(fromBar, toBar)).catch(() => ({ data: { success: false } })));
    }

    const responses = await Promise.all(requests);
    const mainData = responses[0]?.data?.success ? responses[0].data.data : null;
    const barChartData = !isSameRange && responses[1]?.data?.success ? responses[1].data.data : mainData;

    const pt = paymentType || 'receivable';
    let paymentsStats = mainData ? this.adaptPaymentsResponse(mainData, pt, grain) : null;
    if (paymentsStats && barChartData && barChartData !== mainData) {
      const barPoints = (barChartData.points || []).map((p) => this.adaptPaymentsPoint(p, pt));
      paymentsStats = {
        ...paymentsStats,
        points: barPoints,
        barByMonth: this.buildPaymentsBar(barPoints, pt, grain)
      };
    }

    return { paymentsStats };
  }

  adaptPaymentsResponse(data, paymentType, grainHint = null) {
    const totals = data.totals || {};
    const breakdowns = data.breakdowns || {};
    const points = (data.points || []).map((p) => this.adaptPaymentsPoint(p, paymentType));

    const receivable = breakdowns.receivable || {};
    const payable = breakdowns.payable || {};

    const adaptedBreakdowns = { ...breakdowns };
    if (paymentType === 'receivable') {
      if (breakdowns.receivableByStatus) {
        adaptedBreakdowns.receivableByStatus = breakdowns.receivableByStatus;
      } else {
        const confirmed = totals.confirmedReceivable ?? receivable.money?.confirmed ?? 0;
        const outstanding = totals.outstandingReceivable ?? receivable.money?.outstanding ?? 0;
        const totalCount = receivable.totalCount ?? 0;
        adaptedBreakdowns.receivableByStatus = {
          received: { amount: confirmed, count: totalCount },
          outstanding: { amount: outstanding, count: 0 }
        };
      }
    }
    if (paymentType === 'payable') {
      if (breakdowns.payableByStatus) {
        adaptedBreakdowns.payableByStatus = breakdowns.payableByStatus;
      } else {
        const confirmed = totals.confirmedPayable ?? payable.money?.confirmed ?? 0;
        const outstanding = totals.outstandingPayable ?? payable.money?.outstanding ?? 0;
        const totalCount = payable.totalCount ?? 0;
        adaptedBreakdowns.payableByStatus = {
          paid: { amount: confirmed, count: totalCount },
          outstanding: { amount: outstanding, count: 0 }
        };
      }
    }

    const adaptedTotals = { ...totals };
    if (paymentType === 'receivable') {
      adaptedTotals.receivableTotal = totals.totalReceivable ?? receivable.money?.total ?? 0;
    }
    if (paymentType === 'payable') {
      adaptedTotals.payableTotal = totals.totalPayable ?? payable.money?.total ?? 0;
    }

    const grain = grainHint ?? (points[0]?.periodKey?.length >= 10 ? 'day' : 'month');
    const barByMonth = this.buildPaymentsBar(points, paymentType, grain);

    return { totals: adaptedTotals, breakdowns: adaptedBreakdowns, points, barByMonth };
  }

  adaptPaymentsPoint(point, paymentType) {
    const p = { ...point, totals: { ...point.totals }, breakdowns: { ...point.breakdowns } };
    const rec = point.breakdowns?.receivable;
    const pay = point.breakdowns?.payable;
    if (paymentType === 'receivable') {
      p.totals.receivableTotal = point.totals?.totalReceivable ?? rec?.money?.total ?? 0;
      p.breakdowns.receivableByStatus = {
        received: { amount: rec?.money?.confirmed ?? 0, count: rec?.totalCount ?? 0 },
        outstanding: { amount: rec?.money?.outstanding ?? 0, count: 0 }
      };
    }
    if (paymentType === 'payable') {
      p.totals.payableTotal = point.totals?.totalPayable ?? pay?.money?.total ?? 0;
      p.breakdowns.payableByStatus = {
        paid: { amount: pay?.money?.confirmed ?? 0, count: pay?.totalCount ?? 0 },
        outstanding: { amount: pay?.money?.outstanding ?? 0, count: 0 }
      };
    }
    return p;
  }

  async fetchCarriersStats(filterValues, selectedDateRange) {
    const periodPreset = getPeriodPresetFromDateRange(selectedDateRange);
    const customStart = filterValues.dateFrom ? new Date(filterValues.dateFrom).toISOString() : null;
    const customEnd = filterValues.dateTo ? new Date(filterValues.dateTo).toISOString() : null;
    const dateRange = getDateRangeForPeriod(periodPreset, customStart, customEnd);
    const from = dateRange.startDate?.slice(0, 10);
    const to = dateRange.endDate?.slice(0, 10);

    const barChartRange = getBarChartRange(periodPreset, customStart || '', customEnd || '');
    const fromBar = barChartRange.startDate?.slice(0, 10);
    const toBar = barChartRange.endDate?.slice(0, 10);
    const barGrain = getBarChartGrain(periodPreset, customStart || '', customEnd || '');
    const grain = barGrain === 'month' ? 'month' : 'day';

    const scopeType = (filterValues.scopeType === 'user' || filterValues.scopeType === 'customer' || filterValues.scopeType === 'carrier') && filterValues.scopeId ? filterValues.scopeType : 'global';
    const scopeId = scopeType !== 'global' ? String(filterValues.scopeId || '').trim() : null;

    const buildStatsParams = (f, t) => {
      const p = { period: 'range', from: f, to: t, scopeType, grain };
      if (scopeId) p.scopeId = scopeId;
      return p;
    };
    const summaryParams = { from, to, limit: 7 };
    if (scopeType !== 'global' && scopeId) {
      summaryParams.scopeType = scopeType;
      summaryParams.scopeId = scopeId;
    }

    const isSameRange = from === fromBar && to === toBar;
    const requests = [
      this.getStatsCached(buildStatsParams(from, to)).catch(() => ({ data: { success: false } })),
      statsApi.getCarriersSummary(summaryParams).catch(() => ({ data: { success: false } }))
    ];
    if (!isSameRange) {
      requests.push(this.getStatsCached(buildStatsParams(fromBar, toBar)).catch(() => ({ data: { success: false } })));
    }

    const responses = await Promise.all(requests);
    const statsResponse = responses[0];
    const summaryResponse = responses[1];
    const barChartResponse = !isSameRange ? responses[2] : null;

    const statsData = statsResponse?.data?.success ? statsResponse.data.data : null;
    const summaryData = summaryResponse?.data?.success ? summaryResponse.data.data : null;
    const barChartData = isSameRange ? statsData : (barChartResponse?.data?.success ? barChartResponse.data.data : null);

    const points = (barChartData || statsData)?.points || [];
    const barData = this.buildLoadsBar(points, grain);
    const loadsStats = statsData ? {
      byMonth: barData,
      total: statsData.totals?.totalLoads ?? 0,
      byStatus: this.normalizeStatuses(statsData.breakdowns?.loadsByStatus || {})
    } : null;

    const carriersStats = {
      summary: summaryData?.summary || { totalCarriers: 0, byEquipmentType: {} },
      topCarriers: summaryData?.topCarriers || [],
      totalLoads: loadsStats?.total ?? 0,
      byMonth: loadsStats?.byMonth || []
    };

    return { carriersStats, loadsStats };
  }

  async fetchCustomersStats(filterValues, selectedDateRange) {
    const periodPreset = getPeriodPresetFromDateRange(selectedDateRange);
    const customStart = filterValues.dateFrom ? new Date(filterValues.dateFrom).toISOString() : null;
    const customEnd = filterValues.dateTo ? new Date(filterValues.dateTo).toISOString() : null;
    const dateRange = getDateRangeForPeriod(periodPreset, customStart, customEnd);
    const from = dateRange.startDate?.slice(0, 10);
    const to = dateRange.endDate?.slice(0, 10);

    const barChartRange = getBarChartRange(periodPreset, customStart || '', customEnd || '');
    const fromBar = barChartRange.startDate?.slice(0, 10);
    const toBar = barChartRange.endDate?.slice(0, 10);
    const barGrain = getBarChartGrain(periodPreset, customStart || '', customEnd || '');
    const grain = barGrain === 'month' ? 'month' : 'day';

    const scopeType = (filterValues.scopeType === 'user' || filterValues.scopeType === 'customer' || filterValues.scopeType === 'carrier') && filterValues.scopeId ? filterValues.scopeType : 'global';
    const scopeId = scopeType !== 'global' ? String(filterValues.scopeId || '').trim() : null;

    const buildStatsParams = (f, t) => {
      const p = { period: 'range', from: f, to: t, scopeType, grain };
      if (scopeId) p.scopeId = scopeId;
      return p;
    };
    const summaryParams = { from, to, limit: 7 };
    if (scopeType !== 'global' && scopeId) {
      summaryParams.scopeType = scopeType;
      summaryParams.scopeId = scopeId;
    }

    const isSameRange = from === fromBar && to === toBar;
    const requests = [
      this.getStatsCached(buildStatsParams(from, to)).catch(() => ({ data: { success: false } })),
      statsApi.getCustomersSummary(summaryParams).catch(() => ({ data: { success: false } }))
    ];
    if (!isSameRange) {
      requests.push(this.getStatsCached(buildStatsParams(fromBar, toBar)).catch(() => ({ data: { success: false } })));
    }

    const responses = await Promise.all(requests);
    const statsResponse = responses[0];
    const summaryResponse = responses[1];
    const barChartResponse = !isSameRange ? responses[2] : null;

    const statsData = statsResponse?.data?.success ? statsResponse.data.data : null;
    const summaryData = summaryResponse?.data?.success ? summaryResponse.data.data : null;
    const barChartData = isSameRange ? statsData : (barChartResponse?.data?.success ? barChartResponse.data.data : null);

    const points = (barChartData || statsData)?.points || [];
    const barData = this.buildLoadsBar(points, grain);
    const loadsStats = statsData ? {
      byMonth: barData,
      total: statsData.totals?.totalLoads ?? 0,
      byStatus: this.normalizeStatuses(statsData.breakdowns?.loadsByStatus || {}),
      totalProfit: statsData.totals?.totalProfit ?? 0
    } : null;

    const customersStats = {
      summary: summaryData?.summary || { totalCustomers: 0, active: 0, suspended: 0, inactive: 0 },
      topCustomers: summaryData?.topCustomers || [],
      totalLoads: loadsStats?.total ?? 0,
      totalProfit: loadsStats?.totalProfit ?? 0,
      byMonth: loadsStats?.byMonth || []
    };

    return { customersStats, loadsStats };
  }

  async fetchTopCardsStats() {
    const now = new Date();
    const from = new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10);
    const to = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999).toISOString().slice(0, 10);
    
    const params = { period: 'range', from, to, scopeType: 'global' };
    const topCardsResponse = await this.getStatsCached(params);
    
    return {
      topCardsStats: topCardsResponse?.data?.success ? topCardsResponse.data.data : null
    };
  }

  processBarChartData(barChartData, periodPreset, customStart, customEnd) {
    if (!barChartData?.points || !Array.isArray(barChartData.points)) {
      return null;
    }

    const pointsBar = barChartData.points;
    const barChartRange = getBarChartRange(periodPreset, customStart || '', customEnd || '');
    const displayPeriod = getBarChartGrain(periodPreset, customStart || '', customEnd || '');

    const responseMap = new Map();
    pointsBar.forEach((p) => {
      if (p.periodKey) {
        const keyStr = String(p.periodKey);
        responseMap.set(keyStr, p);
        if (displayPeriod === 'month' && keyStr.length >= 7) {
          responseMap.set(keyStr.slice(0, 7), p);
        }
        if (displayPeriod === 'day' && keyStr.length >= 10) {
          responseMap.set(keyStr.slice(0, 10), p);
        }
      }
    });

    let normalizedPoints;
    if (displayPeriod === 'day') {
      const expectedKeys = generateExpectedKeys(displayPeriod, displayPeriod, barChartRange.startDate, barChartRange.endDate);
      const zeroPoint = (key) => ({
        periodKey: key,
        totals: { loadsCreated: 0, totalLoads: 0 },
        breakdowns: { loadsByStatus: { delivered: 0 } }
      });
      normalizedPoints = expectedKeys.map((key) => {
        let point = responseMap.get(key);
        if (!point && key.length >= 10) {
          point = responseMap.get(key.slice(0, 10));
        }
        point = point || zeroPoint(key);
        const t = point.totals || {};
        const b = point.breakdowns || {};
        const byStatus = b.loadsByStatus || {};
        const totalValue = t.totalLoads ?? t.loadsCreated ?? 0;
        return {
          periodKey: key,
          label: formatBarLabel(key, displayPeriod),
          total: totalValue,
          delivered: byStatus.delivered ?? 0
        };
      });
    } else {
      const expectedKeys = generateExpectedKeys(displayPeriod, displayPeriod, barChartRange.startDate, barChartRange.endDate);
      const zeroPoint = (key) => ({
        periodKey: key,
        totals: { loadsCreated: 0, totalLoads: 0 },
        breakdowns: { loadsByStatus: { delivered: 0 } }
      });
      normalizedPoints = expectedKeys.map((key) => {
        let point = responseMap.get(key);
        if (!point && displayPeriod === 'month' && key.length >= 7) {
          point = responseMap.get(key.slice(0, 7));
        }
        if (!point && displayPeriod === 'day' && key.length >= 10) {
          point = responseMap.get(key.slice(0, 10));
        }
        point = point || zeroPoint(key);
        const t = point.totals || {};
        const b = point.breakdowns || {};
        const byStatus = b.loadsByStatus || {};
        const totalValue = t.totalLoads ?? t.loadsCreated ?? 0;
        return {
          periodKey: key,
          label: formatBarLabel(key, displayPeriod),
          total: totalValue,
          delivered: byStatus.delivered ?? 0
        };
      });
    }

    return {
      normalizedPoints,
      barPeriod: displayPeriod,
      pointsBar
    };
  }

  buildPaymentsBar(points, paymentType, grain = 'month') {
    if (!Array.isArray(points) || points.length === 0) return [];
    const isDay = grain === 'day';
    const now = new Date();
    const maxMonthKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;

    const processPoint = (p) => {
      const rec = p.breakdowns?.receivableByStatus;
      const pay = p.breakdowns?.payableByStatus;
      const recTotal = p.totals?.receivableTotal ?? 0;
      const payTotal = p.totals?.payableTotal ?? 0;
      const received = (rec?.received?.amount ?? 0) + (rec?.['partially received']?.amount ?? 0);
      const paid = (pay?.paid?.amount ?? 0) + (pay?.['partially paid']?.amount ?? 0);
      return { recTotal, payTotal, received, paid };
    };

    if (isDay) {
      return points
        .filter((p) => {
          const pk = p.periodKey ? String(p.periodKey) : '';
          if (pk.length < 10) return false;
          const monthKey = pk.slice(0, 7);
          return monthKey <= maxMonthKey;
        })
        .sort((a, b) => (a.periodKey || '').localeCompare(b.periodKey || ''))
        .slice(-7)
        .map((p) => {
          const { recTotal, payTotal, received, paid } = processPoint(p);
          const periodKey = p.periodKey ? String(p.periodKey) : '';
          return {
            month: periodKey,
            monthLabel: formatPaymentBarLabel(periodKey, 'day'),
            received,
            paid,
            receivableTotal: recTotal,
            payableTotal: payTotal
          };
        });
    }

    const monthMap = {};
    points.forEach((p) => {
      const periodKey = p.periodKey ? String(p.periodKey) : '';
      const monthKey = periodKey.length >= 7 ? periodKey.slice(0, 7) : periodKey;
      if (!monthKey || monthKey > maxMonthKey) return;
      if (!monthMap[monthKey]) {
        monthMap[monthKey] = {
          month: monthKey,
          monthLabel: formatPaymentBarLabel(monthKey, 'month'),
          received: 0,
          paid: 0,
          receivableTotal: 0,
          payableTotal: 0
        };
      }
      const { recTotal, payTotal, received, paid } = processPoint(p);
      monthMap[monthKey].received += received;
      monthMap[monthKey].paid += paid;
      monthMap[monthKey].receivableTotal += recTotal;
      monthMap[monthKey].payableTotal += payTotal;
    });
    return Object.keys(monthMap)
      .sort()
      .slice(-7)
      .map((k) => monthMap[k]);
  }

  buildLoadsBar(points, grain = 'month') {
    if (!Array.isArray(points) || points.length === 0) return [];
    const isDay = grain === 'day';
    const now = new Date();
    const maxMonthKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;

    const processPoint = (p) => {
      const t = p.totals || {};
      const b = p.breakdowns || {};
      const byStatus = b.loadsByStatus || {};
      const total = t.totalLoads ?? t.loadsCreated ?? 0;
      const delivered = byStatus.delivered ?? 0;
      const taken = (byStatus.dispatched ?? 0) + (byStatus.pickedUp ?? 0) + delivered + (byStatus.onHold ?? 0);
      return { total, delivered, taken };
    };

    if (isDay) {
      return points
        .filter((p) => {
          const pk = p.periodKey ? String(p.periodKey) : '';
          if (pk.length < 10) return false;
          const monthKey = pk.slice(0, 7);
          return monthKey <= maxMonthKey;
        })
        .sort((a, b) => (a.periodKey || '').localeCompare(b.periodKey || ''))
        .slice(-7)
        .map((p) => {
          const { total, delivered, taken } = processPoint(p);
          const periodKey = p.periodKey ? String(p.periodKey) : '';
          return {
            month: periodKey,
            monthLabel: formatPaymentBarLabel(periodKey, 'day'),
            total,
            delivered,
            taken
          };
        });
    }

    const monthMap = {};
    points.forEach((p) => {
      const periodKey = p.periodKey ? String(p.periodKey) : '';
      const monthKey = periodKey.length >= 7 ? periodKey.slice(0, 7) : periodKey;
      if (!monthKey || monthKey > maxMonthKey) return;
      if (!monthMap[monthKey]) {
        monthMap[monthKey] = {
          month: monthKey,
          monthLabel: formatPaymentBarLabel(monthKey, 'month'),
          total: 0,
          delivered: 0,
          taken: 0
        };
      }
      const { total, delivered, taken } = processPoint(p);
      monthMap[monthKey].total += total;
      monthMap[monthKey].delivered += delivered;
      monthMap[monthKey].taken += taken;
    });
    return Object.keys(monthMap)
      .sort()
      .slice(-7)
      .map((k) => monthMap[k]);
  }

  buildByMonthFromPoints(points, barPeriod) {
    if (!Array.isArray(points) || points.length === 0) return [];
    const monthMap = {};
    const now = new Date();
    const maxMonthKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
    points.forEach((p) => {
      const periodKey = p.periodKey ? String(p.periodKey) : '';
      const monthKey = periodKey.length >= 7 ? periodKey.slice(0, 7) : periodKey;
      if (!monthKey || monthKey > maxMonthKey) return;
      if (!monthMap[monthKey]) {
        const [y, m] = monthKey.split('-');
        const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
        monthMap[monthKey] = {
          month: monthKey,
          monthLabel: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          total: 0,
          delivered: 0,
          taken: 0
        };
      }
      const t = p.totals || {};
      const b = p.breakdowns || {};
      const byStatus = b.loadsByStatus || {};
      const totalVal = t.totalLoads ?? t.loadsCreated ?? 0;
      const deliveredVal = byStatus.delivered ?? 0;
      const takenVal = (byStatus.dispatched ?? 0) + (byStatus.pickedUp ?? 0) + deliveredVal + (byStatus.onHold ?? 0);
      monthMap[monthKey].total += totalVal;
      monthMap[monthKey].delivered += deliveredVal;
      monthMap[monthKey].taken += takenVal;
    });
    return Object.keys(monthMap)
      .sort()
      .slice(-7)
      .map((k) => monthMap[k]);
  }

  normalizeStatuses(byStatus) {
    return {
      Listed: byStatus.listed ?? 0,
      Dispatched: byStatus.dispatched ?? 0,
      'Picked Up': byStatus.pickedUp ?? 0,
      Delivered: byStatus.delivered ?? 0,
      'On Hold': byStatus.onHold ?? 0,
      Cancelled: byStatus.cancelled ?? 0,
      Expired: byStatus.expired ?? 0
    };
  }
}

export default new StatisticsPageService();
